import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useEmergencyCategoryStore } from "../../../store/masterDataStore";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

export type emergencyCategoryFormData = {
  categoryName: string;
  categoryDescription: string;
};

const emptyForm = {
  categoryName: "",
  categoryDescription: "",
};

const EmergencyCategory: React.FC = () => {
  const {
    emergencyCategory,
    createEmergencyCategory,
    deleteEmergencyCategory,
    fetchAllEmergencyCategory,
    loading,
    updateEmergencyCategory,
    fetchEmergencyCategoryById,
  } = useEmergencyCategoryStore();
  const [rowData, setRowData] = useState<any[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] =
    useState<emergencyCategoryFormData>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode && editId) {
        response = await updateEmergencyCategory({ ...formData }, editId);
      } else {
        response = await createEmergencyCategory(formData);
      }

      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        await fetchAllEmergencyCategory();
      }
    } catch (error) {
      console.error("Error creating emergency category:", error);
    }
  };
  const handleEdit = useCallback(
    async (data: any) => {
      const res = await fetchEmergencyCategoryById(data._id);
      if (res) {
        setFormData({
          categoryName: res.categoryName,
          categoryDescription: res.categoryDescription,
        });
        setEditId(data._id);
        setIsEditMode(true);
        setModalOpen(true);
      }
    },
    [fetchEmergencyCategoryById]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this category?"
      );
      if (confirmed) {
        await deleteEmergencyCategory(id);
      }
    },
    [deleteEmergencyCategory]
  );

  useEffect(() => {
    const resData = emergencyCategory.map((cat: any) => {
      return {
        ...cat,
      };
    });
    setRowData(resData);
  }, [emergencyCategory]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchAllEmergencyCategory();
    };
    fetchData();
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "categoryName",
        headerName: "Category Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "categoryName",
      },
      {
        field: "createdAt",
        headerName: "Created At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "createdAt",
        valueGetter: (params: any) =>
          new Date(params.data?.createdAt).toLocaleDateString("en-US") || "NA",
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        tooltipField: "updatedAt",
        valueGetter: (params: any) =>
          new Date(params.data?.updatedAt).toLocaleDateString("en-US") || "NA",
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: () => ({
          padding: "10px 15px 0 0",
          textAlign: "left",
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "clip",
        }),
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Emergency Category"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Emergency Category"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    [handleEdit, handleDelete]
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Emergency Category</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setFormData(emptyForm);
              setEditId(null);
              setIsEditMode(false);
              setModalOpen(true);
            }}
          >
            <Plus size={20} /> Add Category
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Emergency categories added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative"
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode ? "Update Category" : "Create Category"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="px-6 pb-6 flex flex-col gap-6">
              {/* Category Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="categoryName"
                  className="text-sm font-medium text-gray-700"
                >
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="categoryName"
                  name="categoryName"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.categoryName}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  required
                  aria-required="true"
                  aria-label="Category Name"
                />
              </div>

              {/* Category Description */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="categoryDescription"
                  className="text-sm font-medium text-gray-700"
                >
                  Category Description
                </label>
                <textarea
                  id="categoryDescription"
                  name="categoryDescription"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.categoryDescription}
                  onChange={handleChange}
                  placeholder="Enter category description (optional)"
                  rows={3}
                  aria-label="Category Description"
                />
              </div>
            </div>

            {/* Form Actions Footer */}
            <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
              >
                {isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default EmergencyCategory;